import React from "react";
import { Box, Skeleton, Checkbox } from "@mui/material";
import { inferIconForColumn, IconMap } from "./Formatters/Formatters";

const CheckboxHeader = (): React.ReactElement => {
  return <Checkbox sx={{ padding: 0 }} />;
};

const CheckboxFormatter = (): React.ReactElement => {
  return (
    <Skeleton
      animation="wave"
      variant="rectangular"
      sx={{
        bgcolor: "var(--grey-grey-250-light-mode, #E4EAF3)",
        borderRadius: "var(--main-spacing-4, 0.25rem)",
        height: "1.25rem",
        width: "1.25rem",
      }}
    />
  );
};

interface ShimmerColDef {
  id?: string | number;
  name?: string;
  field?: string;
  formatterType?: string;
  filterType?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flexSizing?: string;
  hidden?: boolean;
  headerRenderer?: () => React.ReactElement | string | null;
  formatter?: () => React.ReactElement | null;
}

interface GridShimmerProps {
  columnDefinitions?: ShimmerColDef[];
  rowHeight?: number;
  rowCount?: number;
  enableCheckbox?: boolean;
}

export const GridShimmer: React.FC<GridShimmerProps> = ({
  columnDefinitions = [],
  rowHeight,
  rowCount = 10,
  enableCheckbox = true,
}) => {
  const checkboxColumn: ShimmerColDef = {
    id: "__checkbox",
    name: "",
    width: 50,
    flexSizing: "0 0 50px",
    headerRenderer: CheckboxHeader,
    formatter: CheckboxFormatter,
  };

  const colLength = columnDefinitions.length;

  const filteredColumns = columnDefinitions.filter((col) => !col.hidden);
  if (filteredColumns.length === 0) return;
  let visibleColumns = [...(enableCheckbox ? [checkboxColumn] : []), ...filteredColumns];

  if (colLength > 8) {
    visibleColumns = visibleColumns.slice(0, 8);
  }

  const getColFlex = (col: ShimmerColDef) => {
    if (col.flexSizing) return col.flexSizing;
    const basis = col.width ?? col.minWidth ?? col.maxWidth ?? 150;
    return `1 1 ${basis / 16}rem`;
  };

  const shimmerRows = Array.from({ length: rowCount }, (_, i) => ({
    id: `shimmer-row-${Date.now()}-${i}`,
  }));

  return (
    <div
      style={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: "var(--Background-background-primary, #FFF)",
        border: "1px solid var(--Stroke-stroke-primary, #C6D8EC)",
        borderRadius: "0.25rem",
        display: "block",
      }}
      role="status"
      aria-label="Loading grid data"
    >
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          backgroundColor: "var(--Background-background-primary, #FFF)",
        }}
      >
        {/* --- STATIC HEADER ROW --- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            minHeight: "2.5rem",
            backgroundColor: "var(--Header-header-primary, #F7FBFF)",
            boxSizing: "border-box",
            paddingInlineStart: "0.125rem",
            borderBottom: "1px solid var(--Stroke-stroke-primary, #C6D8EC)",
          }}
        >
          {visibleColumns.map((col, index: number) => (
            <Box
              key={`shimmer-header-${col.id}`}
              sx={{
                flex: getColFlex(col),
                display: "flex",
                alignItems: "center",
                justifyContent: col.id === "__checkbox" ? "center" : "flex-start",
                padding: "0.5rem 1rem",
                boxSizing: "border-box",

                borderInlineStart: index > 0 ? "1px solid var(--Stroke-stroke-primary, #C6D8EC)" : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "var(--Text-text-primary, #192048)",
                fontSize: "0.875rem",
                fontWeight: 600,
                lineHeight: "120%",
              }}
            >
              {col.headerRenderer
                ? (() => {
                    const rendered = col.headerRenderer!();
                    if (typeof rendered === "string") {
                      return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
                    }
                    return rendered;
                  })()
                : col.id === "__checkbox"
                  ? null
                  : (() => {
                      const iconKey = inferIconForColumn({
                        field: col.field,
                        formatterType: col.formatterType,
                        filterType: col.filterType,
                        name: col.name,
                      });
                      const iconSrc = iconKey ? IconMap[iconKey] : undefined;
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            maxWidth: "100%",
                            overflow: "hidden",
                          }}
                        >
                          {iconSrc && (
                            <img src={iconSrc} style={{ width: "1rem", height: "1rem", flexShrink: 0 }} alt="" />
                          )}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {typeof col.name === "string" ? col.name : ""}
                          </span>
                        </div>
                      );
                    })()}
            </Box>
          ))}
        </Box>

        {shimmerRows.map((row, rowIndex: number) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              height: rowHeight != null ? `${rowHeight / 16}rem` : undefined,
              boxSizing: "border-box",

              borderBottom:
                rowIndex < shimmerRows.length - 1 ? "1px solid var(--Stroke-stroke-primary, #C6D8EC)" : "none",

              marginTop: "-1px",
            }}
          >
            {visibleColumns.map((col, index: number) => (
              <Box
                key={`shimmer-cell-${row.id}-${col.id}`}
                sx={{
                  flex: getColFlex(col),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: col.id === "__checkbox" ? "center" : "flex-start",
                  padding: "1.25rem 1rem",
                  boxSizing: "border-box",
                  height: "100%",

                  borderInlineStart: index > 0 ? "1px solid var(--Stroke-stroke-primary, #C6D8EC)" : "none",
                }}
              >
                {col.id === "__checkbox" ? (
                  (col.formatter?.() ?? null)
                ) : (
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    sx={{
                      bgcolor: "var(--grey-grey-250-light-mode, #E4EAF3)",
                      borderRadius: "var(--main-spacing-4, 0.25rem)",
                      height: "65%",
                      width: "100%",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </div>
  );
};

export default GridShimmer;
